import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { FC, useEffect, useRef } from 'react'
import { GRAY_700, GREEN_100, GREEN_700 } from 'constants/styling/theme'
import { Stack, Typography } from '@mui/material'

import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import { MUIButton } from 'components/common/MUIButton'
import { useEmbedClose } from 'utils/embed/useEmbedClose'
import { useTranslation } from 'react-i18next'

/** Long enough to read the confirmation, short enough that nobody waits on a dead screen. */
const AUTO_CLOSE_AFTER_SECONDS = 5

/** The repo has no visually-hidden helper and @mui/utils is not a declared dependency of this app. */
const VISUALLY_HIDDEN_SX = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
} as const

/**
 * @component
 * Terminal "thank you" screen shown in the embedded purchase flow once the order is placed (ISSUE-2377).
 * Offers a Close button that posts BACKBONE_CLOSE to the Propstack parent page (ISSUE-3101), which
 * dismisses the iframe and navigates the broker to their /media tab. The contract forbids a wildcard
 * targetOrigin, so the button is hidden when no allowlisted parent origin can be resolved.
 * Outside the embed this state never renders — the dashboard redirect handles it.
 *
 * The screen also closes itself after a countdown (ISSUE-3171) through that same path, so the
 * countdown is only shown when the close is actually available.
 *
 * @example <EmbedOrderConfirmation />
 */
export const EmbedOrderConfirmation: FC = () => {
  const { t } = useTranslation(['embed'])

  const { canClose, handleClose, secondsUntilAutoClose } = useEmbedClose(
    AnalyticsEvent.EMBED_ORDER_CONFIRMATION_CLOSE_CLICKED,
    AnalyticsEvent.EMBED_ORDER_CONFIRMATION_CLOSE_UNAVAILABLE,
    {},
    { autoCloseAfterSeconds: AUTO_CLOSE_AFTER_SECONDS }
  )

  // Funnel denominator for the close events: without it a drop in closes is indistinguishable from
  // a drop in orders. `canClose` splits out the brokers who were never offered the affordance.
  const shownLoggedRef = useRef(false)
  useEffect(() => {
    if (shownLoggedRef.current) return
    shownLoggedRef.current = true
    logAnalyticsEvent(AnalyticsEvent.EMBED_ORDER_CONFIRMATION_SHOWN, { canClose })
  }, [canClose])

  return (
    <Stack
      gap={3}
      alignItems="center"
      justifyContent="center"
      data-testid="embed-order-confirmation"
      sx={{ height: '100%', minHeight: '60vh', padding: 4, textAlign: 'center' }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ width: 96, height: 96, borderRadius: '50%', backgroundColor: GREEN_100 }}
      >
        <CheckCircleOutlinedIcon sx={{ fontSize: 48, color: GREEN_700 }} />
      </Stack>

      <Stack gap={1} alignItems="center">
        <Typography variant="h5">{t('order_confirmation.title')}</Typography>
        <Typography variant="body1" color={GRAY_700} sx={{ maxWidth: 480 }}>
          {t('order_confirmation.message')}
        </Typography>
      </Stack>

      {canClose &&
        <MUIButton type="defaultPrimary" variant="contained" onClick={handleClose}>
          {t('order_confirmation.close')}
        </MUIButton>
      }

      {secondsUntilAutoClose !== null &&
        <>
          {/*
            Screen readers get the time limit once, from this status region pinned to the initial
            count so its text never changes. The visible line below ticks every second and is
            aria-hidden, because a live region rewritten per tick announces five times over.
          */}
          <Typography
            variant="body2"
            role="status"
            data-testid="embed-order-confirmation-countdown-announcement"
            sx={VISUALLY_HIDDEN_SX}
          >
            {t('order_confirmation.auto_close_countdown', { count: AUTO_CLOSE_AFTER_SECONDS })}
          </Typography>

          <Typography
            variant="body2"
            color={GRAY_700}
            aria-hidden="true"
            data-testid="embed-order-confirmation-countdown"
          >
            {t('order_confirmation.auto_close_countdown', { count: secondsUntilAutoClose })}
          </Typography>
        </>
      }
    </Stack>
  )
}
